// ANCHOR React
import { FormEvent, useMemo } from 'react';

// ANCHOR React Icons
import { Icon } from 'react-icons-kit';
import { play } from 'react-icons-kit/fa/play';

// ANCHOR Base
import { LabelMedium } from 'baseui/typography';
import { Input } from 'baseui/input';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { Modal } from 'baseui/modal';

// ANCHOR Hooks
import { useConstantCallback } from '@lyon/utils/hooks/useConstantCallback';

// ANCHOR Model
import { ProviderData } from '@lyon/scoped-models/ProviderData';
import { ProviderCourseData, useProviderCourseData } from '@lyon/scoped-models/ProviderCourseData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { MobileView } from '@lyon/scoped-models/MobileView';
import { ProviderContentFilter } from '@lyon/scoped-models/provider/ProviderContentFilter';
import {
  ProviderAboutVideoVisibility,
  useProviderAboutVideoVisibilityShow,
  useProviderAboutVideoVisibilityStateAndHide,
} from '@lyon/scoped-models/provider/ProviderAboutVideoVisibility';
import { ProviderInstructorCheck } from '@lyon/scoped-models/ProviderInstructorCheck';

// ANCHOR Results
import { IProviderResult } from '@lyon/utils/api/results/provider';
import { PublishStatus } from '@lyon/utils/api/results/course';

// ANCHOR Utils
import { formatCountLabel } from '@lyon/utils/extras/formatCountLabel';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';
import { SpacedDivider } from '@lyon/components/utils/Divider';
import { ContentCoverImage } from '@lyon/components/utils/ContentCoverImage';
import { LionVideoPlayer } from '@lyon/components/utils/LionVideoPlayer';
import { LyonProviderContainer } from './LyonProviderContainer';
import { LyonProviderCourses } from './LyonProviderCourses';
import { LyonProviderFeaturedCourses } from './LyonProviderFeaturedCourses';

// ANCHOR Styles
import {
  HEADER,
  SUB_HEADER,
  REGULAR,
  INPUT,
} from './styles';

interface IProps {
  id: string;
  initialData: IProviderResult;
}

const ProviderCoverImage = constant(() => {
  const [coverPhotoUuid, name] = ProviderData.useSelector((state) => [
    state.data?.coverPhotoUuid,
    state.data?.name,
  ]);

  return (
    <Block marginBottom="56px">
      <ContentCoverImage
        name={name}
        image={coverPhotoUuid}
        type="rounded"
      />
    </Block>
  );
});

const PLAY = <Icon size={14} icon={play} />;

const ProviderAboutVideo = constant(() => {
  const [aboutVideoUuid, name] = ProviderData.useSelectors((state) => [
    state.data?.aboutVideoUuid,
    state.data?.name,
  ]);

  const [state, hide] = useProviderAboutVideoVisibilityStateAndHide();

  return (
    <Modal
      onClose={hide}
      closeable
      isOpen={state}
      autoFocus
      role="dialog"
    >
      <LionVideoPlayer
        autoplay
        title={name}
        videoId={aboutVideoUuid}
      />
    </Modal>
  );
});

const ProviderAbout = constant(() => {
  const [description, videoId] = ProviderData.useSelectors(({ data }) => [
    data?.description,
    data?.aboutVideoUuid,
  ]);

  const show = useProviderAboutVideoVisibilityShow();

  if (!description) {
    return null;
  }

  return (
    <Block>
      <LabelMedium overrides={HEADER}>
        About Us
      </LabelMedium>
      <LabelMedium marginTop="16px" overrides={REGULAR}>
        {description}
      </LabelMedium>
      {
        videoId && (
          <LabelMedium marginTop="24px" marginLeft="-8px" overrides={REGULAR}>
            <Button
              kind="tertiary"
              type="button"
              size="compact"
              startEnhancer={PLAY}
              onClick={show}
            >
              Watch our Organization Video
            </Button>
          </LabelMedium>
        )
      }
    </Block>
  );
});

const ProviderAboutContainer = constant(() => (
  <Block marginTop="24px">
    <ProviderAboutVideoVisibility.Provider>
      <ProviderAbout />
      <ProviderAboutVideo />
    </ProviderAboutVideoVisibility.Provider>
  </Block>
));

const ProviderSearch = constant(() => {
  const setCourseSearch = ProviderContentFilter.useSelector(
    ({ handler }) => handler.courseSearch,
  );

  const onCourseSearch = useConstantCallback(
    (event: FormEvent<HTMLInputElement>) => {
      setCourseSearch(event.currentTarget.value);
    },
  );

  return (
    <Input
      onChange={onCourseSearch}
      placeholder="Filter displayed courses by name..."
      size="default"
      overrides={INPUT}
    />
  );
});

const ProviderCourses = constant(() => {
  const description = ProviderData.useSelector(({ data }) => data?.description);
  const isMobile = MobileView.useSelector((state) => state.isMobile);
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);

  const courses = useProviderCourseData();
  const search = ProviderContentFilter.useSelector(({ state }) => state.courseSearch);

  const filteredCourses = useMemo(() => courses?.filter((course) => (
    (isProvider
      || (!course.isArchived
        && [PublishStatus.PUBLISHED, PublishStatus.CLOSED].includes(course.publishStatus)))
    && course.title.toLowerCase().includes(search.toLowerCase())
  )), [courses, search, isProvider]);

  const courseCount = filteredCourses?.length ?? 0;
  const courseLabel = formatCountLabel(courseCount, 'Course', {
    includeCount: false,
  });

  if (description) {
    return (
      <Block marginTop="24px">
        <Block
          display="flex"
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'center'}
          flexDirection={isMobile ? 'column' : 'row'}
          width="100%"
        >
          <LabelMedium overrides={SUB_HEADER}>
            {`${courseLabel} (${courseCount})`}
          </LabelMedium>
          <Block width={isMobile ? 'calc(100% - 4vw)' : undefined}>
            <ProviderSearch />
          </Block>
        </Block>
        <Block marginTop="32px">
          <LyonProviderCourses data={filteredCourses} />
        </Block>
      </Block>
    );
  }

  return null;
});

const ProviderFeaturedCourses = constant(() => {
  const courses = useProviderCourseData();
  const filteredCourses = useMemo(() => courses?.filter((course) => (
    course.isPinned
  )), [courses]);

  if (typeof filteredCourses === 'undefined' || filteredCourses.length === 0) {
    return null;
  }

  return (
    <>
      <SpacedDivider top="64px" bottom="64px" />
      <Block marginTop="24px">
        <LabelMedium overrides={SUB_HEADER}>
          {`Featured (${filteredCourses?.length ?? 0})`}
        </LabelMedium>
        <Block marginTop="32px">
          <LyonProviderFeaturedCourses data={filteredCourses} />
        </Block>
      </Block>
    </>
  );
});

export const LyonProvider = ({ id, initialData }: IProps) => (
  <ProviderCheckManager.Provider providerId={id}>
    <ProviderInstructorCheck.Provider providerId={id}>
      <ProviderData.Provider id={id} initialData={initialData}>
        <ProviderCourseData.Provider id={id}>
          <LyonProviderContainer>
            <ProviderCoverImage />
            <ProviderAboutContainer />
            <ProviderFeaturedCourses />
            <SpacedDivider top="64px" bottom="64px" />
            <ProviderContentFilter.Provider>
              <ProviderCourses />
            </ProviderContentFilter.Provider>
          </LyonProviderContainer>
        </ProviderCourseData.Provider>
      </ProviderData.Provider>
    </ProviderInstructorCheck.Provider>
  </ProviderCheckManager.Provider>
);
